/**
 * ReExtractWarningDialog — safe versioned re-extract confirmation.
 *
 * Release A1 (Feb 2026): re-extract is now NON-DESTRUCTIVE. The current
 * registry, its confirmed rules, HITL queue and manifest facts are
 * preserved. A new pending version is created; on successful completion
 * the ``activate_bep_from_job`` RPC atomically flips ``is_current`` to
 * the new row. Failed runs stay inactive for inspection. Nothing is
 * hard-deleted.
 *
 * The dialog still requires the reviewer to type RE-EXTRACT so a mis-
 * click can't kick off a lengthy background extraction. The copy no
 * longer implies destruction.
 *
 * Fetches ``GET /api/projects/{id}/bep/{registryId}/reextract-preview``
 * for the preserved-row counts + next version number.
 */
import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Info, Loader2 } from 'lucide-react';
import { apiFetch } from '../lib/supabase';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABLE_LABELS = {
  confirmed_rules:     'Confirmed rules',
  bep_rule_candidates: 'Rule candidates (incl. HITL queue)',
  bep_manifest_facts:  'Manifest facts',
  bep_extraction_jobs: 'Extraction job records',
};

export default function ReExtractWarningDialog({
  open, onOpenChange, projectId, registryId, fileName, onConfirm,
}) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [typed, setTyped] = useState('');

  useEffect(() => {
    if (!open || !registryId) return;
    setTyped('');
    setPreview(null);
    setLoading(true);
    (async () => {
      try {
        const resp = await apiFetch(
          `${API_URL}/api/projects/${projectId}/bep/${registryId}/reextract-preview`,
        );
        if (resp.ok) setPreview(await resp.json());
      } catch { /* counts stay unknown; typing gate still applies */ }
      finally { setLoading(false); }
    })();
  }, [open, registryId, projectId]);

  const armed = typed === 'RE-EXTRACT';
  const preserved = preview?.will_preserve || {};
  const nextVersion = preview?.next_document_version || '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="reextract-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Info className="h-5 w-5 text-[hsl(var(--primary))]" />
            Re-extract as a new version
          </DialogTitle>
          <DialogDescription className="text-xs">
            Re-extracts <strong>{fileName || preview?.file_name || 'this BEP'}</strong>
            {preview?.document_version ? <> (current version {preview.document_version})</> : null}
            {' '}as a NEW pending version{nextVersion ? <> (<span className="font-mono">{nextVersion}</span>)</> : null}.
            The current version stays active and usable throughout. If the
            new run succeeds, it atomically becomes the active version. If
            it fails or is refused, the current version remains in place —
            nothing is deleted.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-3">
            <Loader2 className="h-4 w-4 animate-spin" /> Counting current-version data…
          </div>
        ) : preview ? (
          <div className="rounded border border-[hsl(var(--primary))]/25 bg-[hsl(var(--primary))]/5 p-3 space-y-1"
               data-testid="reextract-counts">
            <p className="text-xs font-semibold text-foreground pb-1">
              Preserved on the current version (nothing removed):
            </p>
            {Object.entries(TABLE_LABELS).map(([key, label]) => (
              <div key={key} className="flex justify-between text-sm">
                <span className="text-muted-foreground">{label}</span>
                <span className="font-mono font-semibold" data-testid={`reextract-count-${key}`}>
                  {preserved[key] ?? '—'}
                </span>
              </div>
            ))}
            {(preview.hitl_pending || 0) > 0 && (
              <p className="text-[11px] text-muted-foreground pt-1">
                Includes {preview.hitl_pending} pending HITL item{preview.hitl_pending === 1 ? '' : 's'} — still reviewable on the current version until the new run is promoted.
              </p>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Could not load current-version counts — you can still proceed
            safely; nothing is deleted.
          </p>
        )}

        <div className="space-y-1.5">
          <p className="text-xs text-muted-foreground">
            Type <span className="font-mono font-semibold text-foreground">RE-EXTRACT</span> to confirm:
          </p>
          <Input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="RE-EXTRACT"
            autoFocus
            className="font-mono"
            data-testid="reextract-confirm-input"
          />
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  data-testid="reextract-cancel-btn">
            Cancel
          </Button>
          <Button
            disabled={!armed || loading}
            onClick={() => { onOpenChange(false); onConfirm(); }}
            data-testid="reextract-confirm-btn"
          >
            Create new version &amp; extract
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
